import { useState, useCallback, useEffect, useMemo } from 'react';
import { parse, isValid, subMonths, addMonths, startOfMonth, endOfMonth, format } from 'date-fns';
import { useCalendarData, useCalendarSettings } from '../../../lib';
import { formatDateForInput } from '.';

const fetchMonthRange = async (targetDate: Date, fetch: (range: { start: string; end: string }) => Promise<void>) => {
	const start = format(startOfMonth(targetDate), 'yyyy-MM-dd');
	const end = format(endOfMonth(targetDate), 'yyyy-MM-dd');
	await fetch({ start, end });
};

export const useToolbarLogic = (date: Date, view: string, onNavigate: Function, onView: Function) => {
	const { settings } = useCalendarSettings();
	const [inputValue, setInputValue] = useState<string>(() => formatDateForInput(date, view));
	const { fetchAllEventsInRange } = useCalendarData();

	useEffect(() => {
		setInputValue(formatDateForInput(date, view));
	}, [date, view]);

	const inputType = view === 'month' || view === 'agenda' ? 'month' : 'date';

	const handlePrevClick = useCallback(async () => {
		const twoMonthsAgo = subMonths(date, 2);
		await fetchMonthRange(twoMonthsAgo, fetchAllEventsInRange);
		onNavigate('PREV');
	}, [date, onNavigate, fetchAllEventsInRange]);

	const handleNextClick = useCallback(async () => {
		const twoMonthsAhead = addMonths(date, 2);
		await fetchMonthRange(twoMonthsAhead, fetchAllEventsInRange);
		onNavigate('NEXT');
	}, [date, onNavigate, fetchAllEventsInRange]);

	const handleTodayClick = useCallback(() => onNavigate('TODAY'), [onNavigate]);

	const handleDateInputChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			let newDate: Date | null = null;

			try {
				if (inputType === 'month') {
					newDate = parse(value, 'yyyy-MM', new Date());
				} else if (inputType === 'date') {
					newDate = parse(value, 'yyyy-MM-dd', new Date());
				}
			} catch (error) {
				console.error('Invalid date format', error);
			}

			if (newDate && isValid(newDate)) {
				await fetchMonthRange(newDate, fetchAllEventsInRange);
				onNavigate('DATE', newDate);
			}
		},
		[onNavigate, inputType, fetchAllEventsInRange]
	);

	const createViewChangeHandler = useCallback(
		(viewType: string) => () => {
			onView(viewType);
		},
		[onView]
	);

	const viewLabels: { [key: string]: string } = useMemo(
		() => ({
			month: settings.monthButtonLabel || 'Month',
			week: settings.weekButtonLabel || 'Week',
			day: settings.dayButtonLabel || 'Day',
			agenda: settings.agendaButtonLabel || 'Agenda',
		}),
		[settings]
	);

	return {
		inputValue,
		inputType,
		handlePrevClick,
		handleNextClick,
		handleTodayClick,
		handleDateInputChange,
		createViewChangeHandler,
		viewLabels,
	};
};
